import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  TextField,
  Typography,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Stack,
  Checkbox,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import { useReportModal } from "../contexts/ReportContext";
import { useCreateReport, useReport, useSaveReport, useReports, useDeleteReport } from "../api/reportEditor";
import { useExportReportDocx } from "../api/reportExport";
import { streamReportChat, ChatSseEvent } from "../api/reportChat";
import { useReportPlan } from "../api/reportPlan";
import type { ReportPlanState } from "../api/reportPlan";
import { useQueryClient } from "@tanstack/react-query";
import DocumentEditor from "./documentEditor/DocumentEditor";
import { useSnapshots, useDeleteSnapshot } from "../api/snapshots";
import { Snapshot } from "../types/snapshot";

const EMPTY_SNAPSHOTS: Snapshot[] = [];

const PLAN_STATUS_META: Record<ReportPlanState["status"], { label: string; chipColor: string; chipBg: string }> = {
  empty: { label: "Plan drafted", chipColor: "#8fa0b8", chipBg: "rgba(255,255,255,0.12)" },
  in_progress: { label: "In progress", chipColor: "#ffca28", chipBg: "rgba(255,202,40,0.15)" },
  complete: { label: "Complete", chipColor: "#00d4ad", chipBg: "rgba(0,212,173,0.15)" },
};

const createPlanTimestampFormatter = () => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZoneName: "short",
    });
  } catch {
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  }
};

const planTimestampFormatter = createPlanTimestampFormatter();

const formatPlanTimestamp = (isoDate: string | null) => {
  if (!isoDate) return null;
  const timestamp = new Date(isoDate);
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }
  if (planTimestampFormatter) {
    try {
      return planTimestampFormatter.format(timestamp);
    } catch {
      // Ignore and fall back below.
    }
  }
  return timestamp.toLocaleString();
};

interface ChatMsg { id: string; role: "user" | "assistant" | "status"; content: string }

export default function ReportModal() {
  const { open, projectId, reportId, closeModal } = useReportModal();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { mutate: exportDocx, isPending: exporting } = useExportReportDocx();

  // Create a new report if none provided
  const { mutateAsync: createReport } = useCreateReport(projectId || "");
  const [localReportId, setLocalReportId] = useState<string | null>(null);

  const effectiveReportId = localReportId ?? reportId ?? null;
  const { data: report } = useReport(projectId || "", effectiveReportId || "",);
  const { mutate: saveReport } = useSaveReport(projectId || "", effectiveReportId || "");
  const { mutate: deleteReport } = useDeleteReport(projectId || "");
  const { data: reportsList, isLoading: reportsLoading } = useReports(projectId || "", open);
  const { data: snapshotsData, isLoading: snapshotsLoading } = useSnapshots(projectId || undefined, open);
  const { mutate: deleteSnapshot } = useDeleteSnapshot(projectId || undefined);
  const planQueryEnabled = Boolean(open && projectId && effectiveReportId);
  const { data: planState, isFetching: planLoading } = useReportPlan(projectId || "", effectiveReportId || "", planQueryEnabled);

  const qc = useQueryClient();
  const refreshPlan = useCallback(() => {
    if (!projectId || !effectiveReportId) return;
    const queryKey = ["report-plan", projectId, effectiveReportId] as const;
    qc.invalidateQueries({ queryKey });
    qc.refetchQueries({ queryKey, type: "active" });
  }, [projectId, effectiveReportId, qc]);
  const [markdown, setMarkdown] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState<string>("");
  const [titleDirty, setTitleDirty] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [agentWorking, setAgentWorking] = useState(false);
  const [selectedSnapshotIds, setSelectedSnapshotIds] = useState<string[]>([]);
  const [deletingSnapshotId, setDeletingSnapshotId] = useState<string | null>(null);
  const snapshots: Snapshot[] = snapshotsData?.data || EMPTY_SNAPSHOTS;
  const reportLoaded = Boolean(effectiveReportId);

  // chat state
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  const handleCreateReport = async () => {
    if (!projectId) return;
    const data = await createReport("Untitled");
    setLocalReportId(data.id);
    setTitle(data.title || "");
    setMarkdown("");
    setMessages([]);
    setDirty(false);
    setTitleDirty(false);
    setEditingTitle(false);
  };

  const handleSelectReport = async (id: string) => {
    if (!projectId) return;
    setLocalReportId(id);
    setMarkdown("");
    setMessages([]);
    setDirty(false);
    setTitleDirty(false);
    setLoadDialogOpen(false);
    qc.invalidateQueries({ queryKey: ["report", projectId, id] });
  };

  const handleDeleteReport = (id: string) => {
    if (!projectId) return;
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    if (!projectId || !confirmDeleteId) return;
    deleteReport(confirmDeleteId, {
      onSuccess: () => {
        if (effectiveReportId === confirmDeleteId) {
          setLocalReportId(null);
          setMarkdown("");
          setTitle("");
          setMessages([]);
        }
        setConfirmDeleteId(null);
      },
      onError: () => setConfirmDeleteId(null),
    });
  };

  const prevReportIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (effectiveReportId !== prevReportIdRef.current) {
      setMarkdown("");
      setMessages([]);
      setTitleDirty(false);
      prevReportIdRef.current = effectiveReportId || null;
      setSelectedSnapshotIds([]);
      setThreadId(null);
    }
  }, [effectiveReportId]);

  useEffect(() => {
    if (!snapshots.length) {
      setSelectedSnapshotIds((prev) => (prev.length ? [] : prev));
      return;
    }
    setSelectedSnapshotIds((prev) => prev.filter((id) => snapshots.some((snap) => snap.id === id)));
  }, [snapshots]);

  const toggleSnapshotSelection = (id: string) => {
    setSelectedSnapshotIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleDeleteSnapshot = (id: string) => {
    if (!projectId) return;
    setDeletingSnapshotId(id);
    deleteSnapshot(id, {
      onSuccess: () => {
        setSelectedSnapshotIds((prev) => prev.filter((snapshotId) => snapshotId !== id));
      },
      onSettled: () => {
        setDeletingSnapshotId(null);
      },
    });
  };

  // Load markdown once fetched
  useEffect(() => {
    if (!report) return;
    setTitle(report.title || "");
    setDirty(false);
    setTitleDirty(false);
    const nextMarkdown = report.body_markdown ?? report.bodyMarkdown ?? "";
    setMarkdown(nextMarkdown);
  }, [report]);

  // Scroll to bottom when messages update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || !projectId || !effectiveReportId) return;
    const userMsg: ChatMsg = { id: crypto.randomUUID(), role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setAgentWorking(true);

    // start SSE
    const activeThreadId = threadId ?? crypto.randomUUID();
    setThreadId(activeThreadId);
    const dispose = streamReportChat(projectId, effectiveReportId, input, (ev: ChatSseEvent) => {
      if (ev.type === "task_started") {
        refreshPlan();
      } else if (ev.type === "chunk") {
        refreshPlan();
        setMessages((prev) => {
          const newArr = [...prev];
          let last = newArr[newArr.length - 1];
          if (!last || last.role !== "assistant") {
            last = { id: crypto.randomUUID(), role: "assistant", content: "" };
            newArr.push(last);
          }
          last.content += ev.chunk;
          return newArr;
        });
      } else if (ev.type === "complete") {
        refreshPlan();
        // refresh report to pull latest delta saved by agent
        if (projectId && effectiveReportId) {
          qc.invalidateQueries({ queryKey: ["report", projectId, effectiveReportId] });
        }
        setMessages((prev) => {
          const newArr = [...prev];
          const last = newArr[newArr.length - 1];
          if (last && last.role === "assistant") {
            last.content = ev.answer;
          } else {
            newArr.push({ id: crypto.randomUUID(), role: "assistant", content: ev.answer });
          }
          return newArr;
        });
        setAgentWorking(false);
      } else if (ev.type === "error") {
        refreshPlan();
        const fallbackMsg = "The report-writing agent hit an internal error and couldn't finish this task.";
        const rawError = (ev as any).error || fallbackMsg;
        // Show at most first 500 chars to avoid flooding the chat panel
        const errorMessage = rawError.length > 500 ? `${rawError.slice(0, 500)}…` : rawError;
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: errorMessage,
          },
        ]);
        setAgentWorking(false);
      } else if (ev.type === "close") {
        setAgentWorking(false);
      }
    }, { snapshotIds: selectedSnapshotIds, threadId: activeThreadId });
    // dispose when modal closes
    disposers.current.push(dispose);
  };

  const disposers = useRef<(() => void)[]>([]);
  const handleClose = () => {
    disposers.current.forEach((fn) => fn());
    disposers.current = [];
    setLocalReportId(null);
    setAgentWorking(false);
    closeModal();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen
      PaperProps={{ sx:{ bgcolor:'#212b37', color:'white', display:'flex', flexDirection:'column'} }}>
      <DialogTitle sx={{ display:'flex', alignItems:'center', bgcolor:'#212b37', p:1 }}>
        {editingTitle ? (
          <TextField autoFocus value={title} onChange={(e)=>{setTitle(e.target.value); setTitleDirty(true);}}
            onBlur={()=>{ if(titleDirty && projectId && effectiveReportId){ saveReport({title}, {onSuccess: ()=>setTitleDirty(false)}); } setEditingTitle(false);} }
            onKeyDown={(e)=>{ if(e.key==='Enter'){ (e.target as HTMLInputElement).blur(); } }}
            variant="outlined" size="small" sx={{
              bgcolor:'#1f2a36',
              '.MuiOutlinedInput-input':{color:'white'},
              '.MuiOutlinedInput-notchedOutline':{borderColor:'#555'},
              mr:2,
              width:300,
            }} />
        ) : (
          <Box sx={{ display:'flex', alignItems:'center', mr:2, ml:8 }}>
            <Typography variant="h6" sx={{ color:'white', mr:1 }}>
              {title || 'Untitled Report'}
            </Typography>
            {!viewOnly && (
              <Tooltip title="Edit title"><IconButton size="small" onClick={()=> setEditingTitle(true)} sx={{color:'#00d4ad'}}><EditIcon fontSize="small" /></IconButton></Tooltip>
            )}
          </Box>
        )}
        <Box sx={{ flexGrow:1 }} />
        <Tooltip title="Close"><IconButton onClick={handleClose} size="small" sx={{color:'grey.400'}}><CloseIcon fontSize="small" /></IconButton></Tooltip>

      </DialogTitle>
      <DialogContent sx={{ flex:1, display:'flex', bgcolor:'#1b2530', p:1, gap:1, overflow:'hidden', minHeight:0 }}>
        {/* Left vertical toolbar */}
        <Box sx={{ width:48, bgcolor:'#1f2a36', borderRadius:1, p:0.5, display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
          {!viewOnly && (
            <Tooltip title="New report"><span><IconButton onClick={handleCreateReport} size="small" sx={{color:'#00d4ad'}}><NoteAddIcon fontSize="small" /></IconButton></span></Tooltip>
          )}
          <Tooltip title="Load report"><span><IconButton onClick={() => setLoadDialogOpen(true)} size="small" sx={{color:'#00d4ad'}}><FolderOpenIcon fontSize="small" /></IconButton></span></Tooltip>
          {!viewOnly && (
            <Tooltip title="Save" ><span><IconButton disabled={!dirty || saving} onClick={() => {
              if (!projectId || !effectiveReportId) return;
              setSaving(true);
              saveReport({ body_markdown: markdown }, {
                onSuccess: () => { setDirty(false); setSaving(false); },
                onError: () => setSaving(false)
              });
            }} size="small" sx={{color: dirty ? '#00d4ad' : 'grey.500'}}><SaveIcon fontSize="small" /></IconButton></span></Tooltip>
          )}
          <Tooltip title="Export as Word (.docx)"><span><IconButton
            disabled={!effectiveReportId || exporting}
            onClick={() => {
              if (!projectId || !effectiveReportId) return;
              exportDocx({ projectId, reportId: effectiveReportId }, {
                onSuccess: ({ blob, filename }) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                },
              });
            }}
            size="small" sx={{color:'#00d4ad'}}
          ><FileDownloadIcon fontSize="small" /></IconButton></span></Tooltip>
          <Box sx={{ flexGrow:1 }} />
        </Box>

        {/* Middle: Markdown editor */}
        <Box sx={{
          flex:1,
          mr:1,
          bgcolor:'transparent',
          borderRadius:1,
          p:0,
          overflow:'hidden',
          display:'flex',
          flexDirection:'column',
          position:'relative'
        }}>
          {effectiveReportId ? (
            <DocumentEditor
              value={markdown}
              readOnly={viewOnly}
              onChange={(nextMarkdown) => {
                setMarkdown(nextMarkdown);
                setDirty(true);
              }}
            />
          ) : (
            <>
              <Box
                aria-hidden
                sx={{
                  position:'absolute',
                  inset:0,
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center',
                  pointerEvents:'none',
                }}
              >
                <Box
                  sx={{
                    width:'min(900px, 90%)',
                    height:'85%',
                    bgcolor:'#f4f8fb',
                    borderRadius:2,
                    boxShadow:'0 35px 70px rgba(0,0,0,0.4)',
                    filter:'blur(8px)',
                    opacity:0.5,
                  }}
                />
              </Box>
              <Stack
                spacing={2}
                sx={{
                  flex:1,
                  justifyContent:'center',
                  alignItems:'center',
                  textAlign:'center',
                  position:'relative',
                  zIndex:1,
                }}
              >
                <Box
                  sx={{
                    bgcolor:'rgba(15,23,32,0.9)',
                    px:5,
                    py:4,
                    borderRadius:3,
                    boxShadow:'0 25px 60px rgba(0,0,0,0.55)',
                    display:'flex',
                    flexDirection:'column',
                    gap:2,
                    minWidth:360,
                  }}
                >
                  <Typography variant="h6" sx={{ color:'white', fontWeight:600 }}>No report loaded</Typography>
                  <Typography sx={{ color:'rgba(255,255,255,0.75)' }}>
                    Use the toolbar to create a new report or load an existing one.
                  </Typography>
                  <Box sx={{ display:'flex', justifyContent:'center', gap:1, mt:1 }}>
                    {!viewOnly && (
                      <Button variant="contained" onClick={handleCreateReport}>New report</Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => setLoadDialogOpen(true)}
                      sx={{
                        color:'white',
                        borderColor:'rgba(255,255,255,0.6)',
                        '&:hover': {
                          borderColor:'white',
                          backgroundColor:'rgba(255,255,255,0.08)'
                        }
                      }}
                    >
                      Load
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </>
          )}
        </Box>
        {/* Snapshots column (feeds the agent — hidden for view-only users) */}
        {!viewOnly && (
        <Box sx={{ width:320, bgcolor:'#222e3c', borderRadius:1, display:'flex', flexDirection:'column', border:'1px solid rgba(255,255,255,0.06)', filter: reportLoaded ? 'none' : 'blur(4px)', opacity: reportLoaded ? 1 : 0.4, pointerEvents: reportLoaded ? 'auto' : 'none', transition: 'filter 0.2s ease, opacity 0.2s ease' }}>
          <Box sx={{ p:1.25, borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }}>Snapshots for agent</Typography>
            <Chip size="small" label={`${selectedSnapshotIds.length}`} sx={{ bgcolor:'rgba(0,212,173,0.15)', color:'#00d4ad', height:22 }} />
          </Box>
          <Box sx={{ flexGrow:1, overflowY:'auto', p:1 }}>
            {snapshotsLoading ? (
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:1 }}>
                <CircularProgress size={16} sx={{ color:'rgba(255,255,255,0.7)' }} />
                <Typography variant="caption" color="text.secondary">Loading snapshots…</Typography>
              </Box>
            ) : snapshots.length ? (
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
                {snapshots.map((snapshot) => {
                  const checked = selectedSnapshotIds.includes(snapshot.id);
                  const deleting = deletingSnapshotId === snapshot.id;
                  return (
                    <Box
                      key={snapshot.id}
                      sx={{
                        display:'flex',
                        gap:0.75,
                        alignItems:'flex-start',
                        bgcolor: checked ? 'rgba(0,212,173,0.1)' : 'rgba(255,255,255,0.03)',
                        border:'1px solid rgba(255,255,255,0.07)',
                        borderRadius:1,
                        p:0.75,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleSnapshotSelection(snapshot.id)}
                        sx={{ color:'#00d4ad', '&.Mui-checked':{ color:'#00d4ad' }, mt:0.25 }}
                      />
                      <Box sx={{ flex:1, minWidth:0 }}>
                        <Typography variant="body2" sx={{ fontWeight:600 }} noWrap>
                          {snapshot.description || 'Untitled snapshot'}
                        </Typography>
                        {snapshot.mitigationStrategyName && (
                          <Chip
                            size="small"
                            label={snapshot.mitigationStrategyName}
                            sx={{
                              bgcolor:'rgba(0,212,173,0.15)',
                              color:'#00d4ad',
                              height:22,
                              mt:0.5,
                              fontWeight:600,
                            }}
                          />
                        )}
                        <Box
                          component="img"
                          src={snapshot.imageUrl}
                          alt={snapshot.description || 'Snapshot'}
                          sx={{ width:'100%', maxHeight:90, objectFit:'cover', borderRadius:0.5, mt:0.5, border:'1px solid rgba(255,255,255,0.08)' }}
                        />
                      </Box>
                      <Tooltip title="Delete snapshot">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSnapshot(snapshot.id)}
                            disabled={deleting}
                            sx={{ color: deleting ? 'rgba(255,255,255,0.7)' : '#ff6b6b' }}
                          >
                            {deleting ? (
                              <CircularProgress size={16} sx={{ color:'#00d4ad' }} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="white">
                No snapshots yet. Use the camera icon in the top bar to capture the current view.
              </Typography>
            )}
          </Box>
        </Box>
        )}

        {/* Right: Chat panel (agent — hidden for view-only users) */}
        {!viewOnly && (
        <Box sx={{ width:360, bgcolor:'#273341', borderRadius:1, display:'flex', flexDirection:'column', filter: reportLoaded ? 'none' : 'blur(4px)', opacity: reportLoaded ? 1 : 0.4, pointerEvents: reportLoaded ? 'auto' : 'none', transition: 'filter 0.2s ease, opacity 0.2s ease' }}>
          <Box sx={{ p:1.25, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }}>Chat with agent</Typography>
          </Box>
          <PlanStatusPanel plan={planState} loading={planLoading && planQueryEnabled} />
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 1 }}>
            {messages.map((m) => {
              if (m.role === "status") {
                return (
                  <Typography key={m.id} variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", my: 0.5 }}>
                    {m.content}
                  </Typography>
                );
              }
              const isUser = m.role === "user";
              return (
                <Box
                  key={m.id}
                  sx={{
                    mb: 1,
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "85%",
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isUser ? "#00d4ad" : "#1f2a36",
                      color: isUser ? "#0b1a24" : "white",
                      borderTopRightRadius: isUser ? 0.5 : 2,
                      borderTopLeftRadius: isUser ? 2 : 0.5,
                    }}
                  >
                    <Typography variant="body2" whiteSpace="pre-wrap">
                      {m.content}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {agentWorking && (
              <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", gap:1, color:"rgba(255,255,255,0.7)", my:1 }}>
                <CircularProgress size={16} sx={{ color:"inherit" }} />
                <Typography variant="caption" color="inherit">Working…</Typography>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>
          <Box sx={{ p:1, display:'flex', flexDirection:'column', gap:0.75 }}>
            <Chip
              size="small"
              label={`${selectedSnapshotIds.length} snapshot${selectedSnapshotIds.length === 1 ? '' : 's'} selected`}
              sx={{
                bgcolor:'rgba(255,255,255,0.12)',
                color:'white',
                alignSelf:'flex-start',
              }}
            />
            <Box sx={{ display:'flex', gap:1, alignItems:'flex-end' }}>
              <TextField
                size="small"
                fullWidth
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type a message..."
                multiline
                minRows={1}
                maxRows={4}
                disabled={agentWorking}
                sx={{
                  bgcolor:'white',
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
              />
              <IconButton
                sx={{
                  color: '#1b2530',
                  bgcolor: 'white',
                  borderRadius: '8px',
                  width: 40,
                  height: 40,
                  border: '1px solid rgba(0,0,0,0.1)',
                  '&:hover': { bgcolor: '#f7f7f7' },
                  opacity: !input.trim() || !effectiveReportId || agentWorking ? 0.4 : 1,
                }}
                onClick={handleSend}
                disabled={!input.trim() || !effectiveReportId || agentWorking}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
        )}
      </DialogContent>

      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select a report</DialogTitle>
        <DialogContent dividers>
          {reportsLoading ? (
            <Box sx={{ display:'flex', justifyContent:'center', py:3 }}>
              <CircularProgress />
            </Box>
          ) : (reportsList?.length ? (
            <List>
              {reportsList.map((item) => (
                <ListItem key={item.id}
                  secondaryAction={
                    <Box>
                      <Tooltip title="Load">
                        <IconButton edge="end" onClick={() => handleSelectReport(item.id)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      {!viewOnly && (
                        <Tooltip title="Delete">
                          <IconButton edge="end" onClick={() => handleDeleteReport(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemText primary={item.title || 'Untitled'} secondary={item.updated_at ? new Date(item.updated_at).toLocaleString() : null} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No reports yet.</Typography>
          ))}
        </DialogContent>
        <Box sx={{ display:'flex', justifyContent:'flex-end', p:2 }}>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </Box>
      </Dialog>

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete report?</DialogTitle>
        <DialogContent>
          <Typography mb={2}>This cannot be undone. Are you sure you want to delete this report?</Typography>
          <Box sx={{ display:'flex', justifyContent:'flex-end', gap:1 }}>
            <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

interface PlanStatusPanelProps {
  plan?: ReportPlanState | null;
  loading: boolean;
}

function PlanStatusPanel({ plan, loading }: PlanStatusPanelProps) {
  const [persistedPlan, setPersistedPlan] = useState<ReportPlanState | null>(null);

  useEffect(() => {
    if (plan) {
      setPersistedPlan(plan);
    }
  }, [plan]);

  if (!plan && !persistedPlan && loading) {
    return (
      <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Loading plan…</Typography>
      </Box>
    );
  }

  const displayPlan = plan ?? persistedPlan;
  if (!displayPlan) {
    return null;
  }

  const meta = PLAN_STATUS_META[displayPlan.status];
  const items = displayPlan.items;
  const updatedAtLabel = formatPlanTimestamp(displayPlan.updatedAt);

  return (
    <Box sx={{ px: 1.25, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(0,0,0,0.15)' }}>
      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {displayPlan.title || 'Agent plan'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {displayPlan.totalCount ? `${displayPlan.completedCount}/${displayPlan.totalCount} steps complete` : 'Plan ready'}
          </Typography>
          {updatedAtLabel && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
              Updated {updatedAtLabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {loading && <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} />}
          <Chip
            size="small"
            label={meta.label}
            sx={{
              backgroundColor: meta.chipBg,
              color: meta.chipColor,
              height: 24,
              fontWeight: 600,
            }}
          />
        </Box>
      </Stack>
      <Stack spacing={0.5} mt={1} sx={{ maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
        {items.length ? (
          items.map((item, idx) => (
            <Box
              key={`${item.text}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(0,0,0,0.2)',
                opacity: item.completed ? 0.65 : 1,
              }}
            >
              <Box
                sx={{
                  flex: '0 0 12px',
                  flexShrink: 0,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.4)',
                  backgroundColor: item.completed ? '#00d4ad' : 'transparent',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  textDecoration: item.completed ? 'line-through' : 'none',
                  whiteSpace: 'normal',
                }}
              >
                {`${idx + 1}. ${item.text}`}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Plan is ready for the agent to populate.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
